// "Ler planilha da Gran" (#6116): sobe o Excel de conferência da Gran, mostra a prévia do que
// muda na lista e só grava quando a pessoa confirma. Quem não veio na planilha NÃO sai sozinho:
// começa desmarcado e a pessoa marca quem tirar (decisão do Victor, 28/09/2026).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileSpreadsheet, Loader2, Upload, X } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logAction } from '../../../src/lib/logger';
import type { ItemPlanilhaGran, UcPlanilhaGran } from './planilhaGranModel';
import { lerPlanilhaConferencia, planejarImportacao, type PlanoImportacao } from './lerPlanilhaConferencia';
import * as api from './planilhaGranApi';

interface Props {
  empresaId: string;
  itens: ItemPlanilhaGran[];
  ucs: UcPlanilhaGran[];
  onClose: () => void;
  onGravou: () => void;
}

const hojeISO = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

const LerPlanilhaGranModal: React.FC<Props> = ({ empresaId, itens, ucs, onClose, onGravou }) => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [arquivo, setArquivo] = useState<string>('');
  const [lendo, setLendo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState('');
  const [plano, setPlano] = useState<PlanoImportacao | null>(null);
  const [tirar, setTirar] = useState<Set<number>>(new Set());
  const inputArquivoRef = useRef<HTMLInputElement>(null);

  // Esc fecha só esta janela (a Planilha Gran por trás também escuta Esc).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !gravando) { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, gravando]);

  // Foco inicial no controle principal (escolher arquivo) — quem abre pelo teclado ou
  // leitor de tela não precisa procurar por onde começar.
  useEffect(() => { inputArquivoRef.current?.focus(); }, []);

  const ler = async (f: File) => {
    setLendo(true); setErro(''); setPlano(null); setTirar(new Set()); setArquivo(f.name);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await f.arrayBuffer(), { type: 'array' });
      const fonte = f.name.replace(/\.[^.]+$/, '').replace(/\s*\(\d+\)$/, '').replace(/\s+/g, ' ').trim();
      for (const nome of wb.SheetNames) {
        const matriz = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[nome], { header: 1, blankrows: false, raw: true });
        const leitura = lerPlanilhaConferencia(matriz, fonte, hojeISO());
        if (leitura.linhas.length) { setPlano(planejarImportacao(leitura.linhas, itens, ucs)); return; }
      }
      setErro('Não achei a coluna "UC Nova" (ou "UC Antiga") em nenhuma aba. Confira se é a planilha de conferência da Gran.');
    } catch {
      setErro('Não foi possível ler este arquivo. Use o Excel (.xlsx) da planilha da Gran.');
    } finally {
      setLendo(false);
    }
  };

  const removerIds = useMemo(() => (plano ? plano.sair.filter(s => tirar.has(s.itemId)).map(s => s.itemId) : []), [plano, tirar]);
  const origemPorItem = useMemo(() => new Map(itens.map(i => [i.id, i.origem])), [itens]);

  // Resumo do que "Gravar na lista" faz de fato — para a pessoa saber antes de clicar,
  // não só depois (o toast de sucesso repete os mesmos números).
  const resumoGravar = useMemo(() => {
    if (!plano) return '';
    const partes: string[] = [];
    if (plano.atualizar.length) partes.push(`${plano.atualizar.length} atualiza${plano.atualizar.length !== 1 ? 'm' : ''}`);
    if (plano.incluir.length) partes.push(`${plano.incluir.length} entra${plano.incluir.length !== 1 ? 'm' : ''}`);
    if (removerIds.length) partes.push(`${removerIds.length} sa${removerIds.length !== 1 ? 'em' : 'i'}`);
    return partes.join(', ');
  }, [plano, removerIds.length]);

  const gravar = async () => {
    if (!plano) return;
    setGravando(true);
    try {
      const r = await api.importarConferencia(empresaId, plano, removerIds);
      await logAction(user?.id || 'SYSTEM', 'Leu a planilha da Gran na Planilha Gran', { arquivo, ...r, nao_achadas: plano.naoAchadas.map(n => n.numero) });
      addToast(`Planilha da Gran lida: ${r.atualizadas} atualizada(s), ${r.incluidas} incluída(s), ${r.removidas} tirada(s) da lista.`, 'success');
      onGravou();
      onClose();
    } catch (e: any) {
      addToast(e.message, 'error');
    } finally {
      setGravando(false);
    }
  };

  const bloco = 'rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden';
  const titulo = 'px-3 py-1.5 text-[12px] font-semibold bg-gray-50 dark:bg-gray-800/60 text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800';

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in" role="dialog" aria-modal="true" aria-labelledby="ler-planilha-gran-titulo">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !gravando && onClose()} />
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h2 id="ler-planilha-gran-titulo" className="text-[15px] font-bold text-gray-900 dark:text-white">Ler planilha da Gran</h2>
          <button onClick={onClose} disabled={gravando} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Fechar"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-4 pb-3 flex-1 min-h-0 overflow-y-auto space-y-3">
          <p className="text-[12px] text-gray-600 dark:text-gray-300">
            Quem está na planilha de conferência da Gran entra ou é atualizado com a anotação e o que a Gran conferiu. Quem não está aparece para você decidir se sai da lista. Nada é gravado antes de você confirmar.
          </p>

          <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-primary/60 cursor-pointer focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary/60">
            {lendo ? <Loader2 className="w-5 h-5 animate-spin text-primary" aria-hidden="true" /> : <FileSpreadsheet className="w-5 h-5 text-primary" aria-hidden="true" />}
            <span className="flex-1 text-[12px] text-gray-700 dark:text-gray-200">{lendo ? 'Lendo o arquivo…' : (arquivo || 'Escolher o Excel da Gran (.xlsx)')}</span>
            <span className="text-[12px] font-semibold text-primary">{arquivo ? 'Trocar' : 'Escolher'}</span>
            <input ref={inputArquivoRef} type="file" accept=".xlsx,.xls" className="sr-only" disabled={lendo || gravando}
              onChange={e => { const f = e.target.files?.[0]; if (f) ler(f); e.target.value = ''; }} />
          </label>

          {erro && <p role="alert" className="text-[12px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{erro}</p>}

          {plano && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" aria-live="polite">
                {[
                  { r: 'Atualizam', v: plano.atualizar.length, c: 'text-gray-900 dark:text-white' },
                  { r: 'Entram', v: plano.incluir.length, c: 'text-emerald-700 dark:text-emerald-400' },
                  { r: 'Saem', v: removerIds.length, c: 'text-red-700 dark:text-red-400' },
                  { r: 'Sem cadastro', v: plano.naoAchadas.length, c: 'text-amber-700 dark:text-amber-400' },
                ].map(k => (
                  <div key={k.r} className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{k.r}</p>
                    <p className={`text-lg font-bold ${k.c}`}>{k.v}</p>
                  </div>
                ))}
              </div>

              {plano.sair.length > 0 && (
                <section className={bloco}>
                  <div className={`${titulo} flex items-center gap-2`}>
                    <h3 className="flex-1">Não vieram na planilha da Gran ({plano.sair.length}) — marque as que devem sair da lista</h3>
                    <button type="button" onClick={() => setTirar(removerIds.length === plano.sair.length ? new Set() : new Set(plano.sair.map(x => x.itemId)))} className="text-[11px] font-semibold text-primary hover:underline">
                      {removerIds.length === plano.sair.length ? 'Desmarcar todas' : 'Marcar todas'}
                    </button>
                  </div>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800 max-h-48 overflow-y-auto">
                    {plano.sair.map(s => (
                      <li key={s.itemId}>
                        <label className="px-3 py-1.5 flex items-center gap-2 text-[12px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40">
                          <input type="checkbox" checked={tirar.has(s.itemId)}
                            onChange={e => setTirar(prev => { const n = new Set(prev); e.target.checked ? n.add(s.itemId) : n.delete(s.itemId); return n; })}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/50" />
                          <span className="font-mono text-gray-700 dark:text-gray-200 w-36 shrink-0">{s.numero}</span>
                          <span className="truncate text-gray-500 dark:text-gray-400 flex-1">{s.grupo}</span>
                          {origemPorItem.get(s.itemId) === 'extra' && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300" title="Incluída à mão na lista">Extra</span>}
                        </label>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {plano.incluir.length > 0 && (
                <section className={bloco}>
                  <h3 className={titulo}>Entram na lista (com o nome do cliente como condomínio)</h3>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800 max-h-48 overflow-y-auto">
                    {plano.incluir.map(n => (
                      <li key={n.ucId} className="px-3 py-1.5 flex items-center gap-2 text-[12px]">
                        <span className="font-mono text-gray-700 dark:text-gray-200 w-36 shrink-0">{n.numero}</span>
                        <span className="truncate text-gray-500 dark:text-gray-400">{n.nome || '—'}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {plano.naoAchadas.length > 0 && (
                <section className={bloco}>
                  <h3 className={titulo}>Sem cadastro no sistema — ficam de fora até a UC ser cadastrada</h3>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800 max-h-40 overflow-y-auto">
                    {plano.naoAchadas.map(n => (
                      <li key={n.linha} className="px-3 py-1.5 flex items-center gap-2 text-[12px]">
                        <span className="text-gray-400 w-14 shrink-0">linha {n.linha}</span>
                        <span className="font-mono text-gray-700 dark:text-gray-200 w-36 shrink-0">{n.numero}</span>
                        <span className="truncate text-gray-500 dark:text-gray-400">{n.nome || '—'}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} disabled={gravando} className="px-3 py-2 text-[12px] font-semibold rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800">Cancelar</button>
          <button onClick={gravar} disabled={!plano || gravando || lendo}
            className="inline-flex items-center gap-2 px-3 py-2 text-[12px] font-semibold rounded-lg bg-primary hover:bg-primary/90 text-white disabled:opacity-40 disabled:cursor-not-allowed">
            {gravando ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Upload className="w-4 h-4" aria-hidden="true" />}
            {resumoGravar ? `Gravar (${resumoGravar})` : 'Gravar na lista'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LerPlanilhaGranModal;
